import { salesErrorMessage } from './types';
import type { LeadStage } from './types';

export function todayIso(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

export function composeErrorMessage(message: string): string {
  if (message.includes('drive_reconnect_required')) {
    // Not "sign out and back in": a normal sign-in never requests offline
    // access, so that cannot mint a refresh token and never fixed this.
    return 'Google Drive needs reconnecting — use Reconnect Google Drive in the account menu.';
  }
  return salesErrorMessage(message);
}

export function stageName(stages: LeadStage[], id: string): string {
  return stages.find((s) => s.id === id)?.name ?? '';
}

/** ISO date string → "Aug 3" */
export function shortDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * A DAY, printed as the day it says it is. "2026-08-01" → "Aug 1", anywhere.
 *
 * shortDate is right for an instant and wrong for a date-only string, and the
 * difference is invisible in this container. `new Date('2026-08-01')` parses
 * as UTC midnight; toLocaleDateString then renders it in the BROWSER's zone,
 * so west of Greenwich it prints the day before. Kyu is in New York, so every
 * date-only string this app renders through shortDate has been one day early
 * on his screen.
 *
 * The whole timeline is UTC by the 20261124000100 convention — activityDayIso
 * slices the UTC date, and the ranges in dashboardRange.ts are built from it.
 * A day that came out of that arithmetic must be printed in the same zone it
 * was computed in, or the label disagrees with the filter that produced it.
 *
 * shortDate is deliberately left alone: it is also called with real timestamps
 * (a lead's created_at), where rendering in the reader's own zone is correct.
 * Which of its callers pass days and which pass instants is an audit, not a
 * line — see docs/open-items.md C1.
 */
export function dayLabel(iso: string): string {
  return new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * The same day, carrying its year. "2025-11-04" → "Nov 4, 2025".
 *
 * `dayLabel` is right where the year is obvious from context — a timeline of
 * this week, a range the reader just picked. A closing date is the opposite
 * case: the question it exists to answer is Kyu's "who are the people that
 * said no THIS YEAR", so a bare "Nov 4" beside a closing reason leaves out the
 * one fact the reader came for.
 *
 * Same UTC pinning as dayLabel and for the same reason — see its comment. Both
 * print a date-only string as the day it says it is, anywhere in the world.
 */
export function dayLabelWithYear(iso: string): string {
  return new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** ISO date string within the last 7 days? */
// ── Dashboard ─────────────────────────────────────────────────────────────────

/**
 * The short form a category wears in a dense row. "Corporate Gifting" is on
 * most rows of the leads table, and at full length it was the widest thing on
 * them; "CG" is what the team already says. Kyu, 2026-08-29: "CG is fine."
 * Everything else keeps its name — the abbreviation is an exception, not a
 * rule, and the full name stays on the cell's title for anyone who hovers.
 */
export function shortCategory(name: string): string {
  return name === 'Corporate Gifting' ? 'CG' : name;
}
